import React from "react";
import {
  Box,
  Flex,
  Button,
  useDisclosure,
  Collapse,
  Spacer,
  Input,
  FormControl,
  FormErrorMessage,
  Textarea,
} from "@chakra-ui/react";
import { IoTrashOutline } from "react-icons/io5";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useEffect } from "react";
import PrimaryHeading from "../../../components/PrimaryHeading";
import FormInput from "../../../components/FormInput";
import FormSelector from "../../../components/FormSelector";
import CollapseIcon from "../../../assets/images/collapse.svg";
import {
  handleSelectChange,
  handleChange,
  handlePassportFileChange,
  handleViewFile,
  removeFile,
} from "../form/utils";
import FormInputFile from "../../../components/FormPassportInputFile";
import PrimaryFormLabel from "../../../components/PrimaryFormLabel";
import { genderData, stateData } from "../constants";
import AlphaInput from "../../../components/AlphaInput";

const PatientAttendantSection = ({
  index,
  name,
  label,
  formData,
  setFormData,
  formErrors,
  setFormErrors,
  onDelete,
  isDeleteVisible,
}) => {
  const dispatch = useDispatch();

  const { embassyData, countryData, passportDetails, passportLabel } =
    useSelector((state) => state.hcf);

  const { isOpen: isSectionOpen, onToggle: toggleSection } = useDisclosure({
    defaultIsOpen: true,
  });

  const formatLabel = (label) => {
    return label.replace(/\s+/g, "").toLowerCase();
  };

  const formattedLabel = formatLabel(label);

  useEffect(() => {
    if (passportDetails && passportLabel && passportLabel === formattedLabel) {
      setFormData((prevData) => ({
        ...prevData,
        [`${formattedLabel}Name`]: passportDetails.name,
        [`${formattedLabel}PassportNumber`]: passportDetails.passport_number,
        [`${formattedLabel}Nationality`]: countryData.find(
          (c) => c.value === passportDetails.nationality
        ),
      }));
      setFormErrors((prevData) => ({
        ...prevData,
        [`${formattedLabel}Name`]: false,
        [`${formattedLabel}PassportNumber`]: false,
        [`${formattedLabel}Nationality`]: false,
      }));
    }
  }, [passportDetails]);

  return (
    <>
      <Flex
        alignItems="center"
        justifyContent="space-between"
        className="collapse-section-header"
      >
        <PrimaryHeading style={{ margin: 0 }}>{name} Details</PrimaryHeading>
        <img
          src={CollapseIcon}
          width="24px"
          style={{
            cursor: "pointer",
            marginLeft: "10px",
            transition: "transform 0.3s ease",
            transform: isSectionOpen ? "rotate(0)" : "rotate(180deg)",
          }}
          onClick={toggleSection}
        />
        <Spacer />
        {isDeleteVisible && label !== "Patient" && (
          <Button
            leftIcon={<IoTrashOutline style={{ fontSize: "15px" }} />}
            colorScheme="red"
            variant="ghost"
            onClick={() => onDelete(index)}
            aria-label="Delete item"
            size="sm"
          >
            Delete
          </Button>
        )}
      </Flex>
      <Collapse in={isSectionOpen} animateOpacity>
        <Box
          display="flex"
          flexDirection="column"
          bgColor="white"
          borderRadius="10px"
        >
          <Box
            display="flex"
            flexDirection={{ base: "column", md: "row" }}
            justifyContent="space-between"
            className="form-box"
          >
            <FormInputFile
              name="Upload Passport"
              label={label}
              formattedLabel={formattedLabel}
              formData={formData}
              setFormData={setFormData}
              formErrors={formErrors}
              setFormErrors={setFormErrors}
              dispatch={dispatch}
              handlePassportFileChange={handlePassportFileChange}
              handleViewFile={handleViewFile}
              removeFile={removeFile}
            />
          </Box>
          <Box
            display="flex"
            flexDirection={{ base: "column", md: "row" }}
            justifyContent="space-between"
            className="form-box"
          >
            <FormInput
              id={`${formattedLabel}Name`}
              label={"Full Name"}
              type="text"
              placeholder={"Enter Full Name"}
              value={formData[`${formattedLabel}Name`]}
              onChange={(e) =>
                handleChange(
                  formData,
                  setFormData,
                  formErrors,
                  setFormErrors,
                  e
                )
              }
              isInvalid={!!formErrors[`${formattedLabel}Name`]}
              errorMessage={formErrors[`${formattedLabel}Name`]}
              isRequired={true}
              flex={{ base: "100%", md: "calc(50% - 10px)" }}
              mr={{ base: "0", md: "20px" }}
            />

            <FormInput
              id={`${formattedLabel}PassportNumber`}
              label="Passport Number"
              type="text"
              placeholder="Enter Passport Number"
              value={formData[`${formattedLabel}PassportNumber`]}
              onChange={(e) =>
                handleChange(
                  formData,
                  setFormData,
                  formErrors,
                  setFormErrors,
                  e
                )
              }
              isInvalid={!!formErrors[`${formattedLabel}PassportNumber`]}
              errorMessage={formErrors[`${formattedLabel}PassportNumber`]}
              isRequired={true}
              flex={{ base: "100%", md: "calc(50% - 10px)" }}
              mr={{ base: "0", md: "0px" }}
            />
          </Box>

          <>
            <Box
              display="flex"
              flexDirection={{ base: "column", md: "row" }}
              justifyContent="space-between"
              className="form-box"
            >
              <FormSelector
                id={`${formattedLabel}Embassy`}
                label="Embassy"
                options={embassyData}
                placeholder="Select Embassy"
                value={formData[`${formattedLabel}Embassy`]}
                onChange={(selectedOption, actionMeta) =>
                  handleSelectChange(
                    formData,
                    setFormData,
                    formErrors,
                    setFormErrors,
                    selectedOption,
                    actionMeta
                  )
                }
                isInvalid={!!formErrors[`${formattedLabel}Embassy`]}
                errorMessage={formErrors[`${formattedLabel}Embassy`]}
                isRequired={true}
                flex={{ base: "100%", md: "calc(50% - 10px)" }}
                mr={{ base: "0", md: "20px" }}
              />

              <FormSelector
                id={`${formattedLabel}Nationality`}
                label="Nationality"
                options={countryData}
                placeholder="Select Nationality"
                value={formData[`${formattedLabel}Nationality`]}
                onChange={(selectedOption, actionMeta) =>
                  handleSelectChange(
                    formData,
                    setFormData,
                    formErrors,
                    setFormErrors,
                    selectedOption,
                    actionMeta
                  )
                }
                isInvalid={!!formErrors[`${formattedLabel}Nationality`]}
                errorMessage={formErrors[`${formattedLabel}Nationality`]}
                isRequired={true}
                flex={{ base: "100%", md: "calc(50% - 10px)" }}
                mr={{ base: "0", md: "0px" }}
              />
            </Box>
          </>

          <>
            <Box
              display="flex"
              flexDirection={{ base: "column", md: "row" }}
              justifyContent="space-between"
              className="form-box"
            >
              <FormControl
                isInvalid={!!formErrors[`${formattedLabel}dob`]}
                errorMessage={formErrors[`${formattedLabel}dob`]}
                flex="50%"
                className="form-input"
                isRequired
                mr={{ base: "0", md: "20px" }}
              >
                <PrimaryFormLabel htmlFor="dob">Date Of Birth</PrimaryFormLabel>
                <Input
                  id={`${formattedLabel}dob`}
                  name={`${formattedLabel}dob`}
                  type="date"
                  value={formData[`${formattedLabel}dob`]}
                  onChange={(e) =>
                    handleChange(
                      formData,
                      setFormData,
                      formErrors,
                      setFormErrors,
                      e
                    )
                  }
                />
              </FormControl>

              <FormSelector
                id={`${formattedLabel}Gender`}
                name={`${formattedLabel}Gender`}
                label="Gender"
                options={genderData}
                placeholder="Select gender"
                value={formData[`${formattedLabel}Gender`]}
                onChange={(selectedOption, actionMeta) =>
                  handleSelectChange(
                    formData,
                    setFormData,
                    formErrors,
                    setFormErrors,
                    selectedOption,
                    actionMeta
                  )
                }
                isInvalid={!!formErrors[`${formattedLabel}Gender`]}
                errorMessage={formErrors[`${formattedLabel}Gender`]}
                isRequired={true}
                flex={{ base: "100%", md: "calc(50% - 10px)" }}
                mr={{ base: "0", md: "0px" }}
              />
            </Box>
            <Box
              display="flex"
              flexDirection={{ base: "column", md: "row" }}
              justifyContent="space-between"
              className="form-box"
            >
              <FormControl
                flex="50%"
                className="form-input"
                isRequired
                mr={{ base: "0", md: "20px" }}
              >
                <PrimaryFormLabel htmlFor="ContactNumber">
                  Contact Number
                </PrimaryFormLabel>
                <Input
                  type="number"
                  id={`${formattedLabel}ContactNumber`}
                  name={`${formattedLabel}ContactNumber`}
                  value={formData[`${formattedLabel}ContactNumber`]}
                  onChange={(e) =>
                    handleChange(
                      formData,
                      setFormData,
                      formErrors,
                      setFormErrors,
                      e
                    )
                  }
                  placeholder="Enter your contact number"
                  isInvalid={!!formErrors[`${formattedLabel}ContactNumber`]}
                  errorMessage={formErrors[`${formattedLabel}ContactNumber`]}
                  maxLength={40}
                  isRequired={true}
                />
              </FormControl>

              <FormControl
                isInvalid={formErrors.Email}
                flex="50%"
                className="form-input"
                isRequired
              >
                <PrimaryFormLabel htmlFor="name">Email</PrimaryFormLabel>
                <Input
                  id={`${formattedLabel}Email`}
                  name={`${formattedLabel}Email`}
                  value={formData[`${formattedLabel}Email`]}
                  onChange={(e) =>
                    handleChange(
                      formData,
                      setFormData,
                      formErrors,
                      setFormErrors,
                      e
                    )
                  }
                  placeholder="Enter email"
                  isRequired={true}
                  isInvalid={!!formErrors[`${formattedLabel}Email`]}
                  errorMessage={formErrors[`${formattedLabel}Email`]}
                />
              </FormControl>
            </Box>
          </>

          <Box
            display="flex"
            flexDirection={{ base: "column", md: "row" }}
            justifyContent="space-between"
            className="form-box"
            mb={"20px"}
          >
            <Box flex="50%" mr={{ base: "0", md: "20px" }}>
              <FormControl isRequired>
                <PrimaryFormLabel htmlFor="Address in Native Country">
                  Address in Native Country
                </PrimaryFormLabel>
                <Textarea
                  id={`${formattedLabel}Address`}
                  name={`${formattedLabel}Address`}
                  value={formData[`${formattedLabel}Address`]}
                  isInvalid={!!formErrors[`${formattedLabel}Address`]}
                  errorMessage={formErrors[`${formattedLabel}Address`]}
                  placeholder="Enter your address here..."
                  onChange={(e) =>
                    handleChange(
                      formData,
                      setFormData,
                      formErrors,
                      setFormErrors,
                      e
                    )
                  }
                />
              </FormControl>
            </Box>

            <Box flex="50%">
              <FormControl isRequired>
                <PrimaryFormLabel htmlFor="Address/Reference in India">
                  Address/Reference in India
                </PrimaryFormLabel>
                <Textarea
                  id={`${formattedLabel}ReferenceAddress`}
                  name={`${formattedLabel}ReferenceAddress`}
                  value={formData[`${formattedLabel}ReferenceAddress`]}
                  isInvalid={!!formErrors[`${formattedLabel}ReferenceAddress`]}
                  errorMessage={formErrors[`${formattedLabel}ReferenceAddress`]}
                  placeholder="Enter your address here..."
                  onChange={(e) =>
                    handleChange(
                      formData,
                      setFormData,
                      formErrors,
                      setFormErrors,
                      e
                    )
                  }
                />
              </FormControl>
            </Box>
          </Box>
          <Box
            display="flex"
            flexDirection={{ base: "column", md: "row" }}
            justifyContent="space-between"
            className="form-box"
          >
            <Box flex="50%" mr={{ base: "0", md: "20px" }}>
              <FormSelector
                id={`${formattedLabel}State`}
                value={formData[`${formattedLabel}State`]}
                isInvalid={!!formErrors[`${formattedLabel}State`]}
                errorMessage={formErrors[`${formattedLabel}State`]}
                isRequired={true}
                label="State"
                name={`${formattedLabel}State`}
                placeholder="Select State"
                options={stateData}
                menuPlacement="top"
                // onChange={handleSelectChange}
                onChange={(selectedOption, actionMeta) =>
                  handleSelectChange(
                    formData,
                    setFormData,
                    formErrors,
                    setFormErrors,
                    selectedOption,
                    actionMeta
                  )
                }
              />
            </Box>
            <Box flex="50%">
              <FormControl flex="50%" className="form-input" isRequired>
                <PrimaryFormLabel htmlFor="ReferenceCity">
                  City
                </PrimaryFormLabel>
                <AlphaInput
                  name={`${formattedLabel}ReferenceCity`}
                  id={`${formattedLabel}ReferenceCity`}
                  value={formData[`${formattedLabel}ReferenceCity`]}
                  onChange={(e) =>
                    handleChange(
                      formData,
                      setFormData,
                      formErrors,
                      setFormErrors,
                      e
                    )
                  }
                  placeholder="Enter your city"
                  isInvalid={!!formErrors[`${formattedLabel}ReferenceCity`]}
                  errorMessage={formErrors[`${formattedLabel}ReferenceCity`]}
                  maxLength={40}
                  isRequired={true}
                />
              </FormControl>
            </Box>
          </Box>

          <Box
            display="flex"
            flexDirection={{ base: "column", md: "row" }}
            justifyContent="space-between"
            className="form-box"
          >
            <Box flex="50%" mr={{ base: "0", md: "20px" }}>
              <FormControl
                // isInvalid={formErrors.pincode}
                className="form-input"
                isRequired
              >
                <PrimaryFormLabel htmlFor="name">Pincode</PrimaryFormLabel>
                <Input
                  name={`${formattedLabel}Pincode`}
                  id={`${formattedLabel}Pincode`}
                  value={formData[`${formattedLabel}Pincode`]}
                  onChange={(e) =>
                    handleChange(
                      formData,
                      setFormData,
                      formErrors,
                      setFormErrors,
                      e
                    )
                  }
                  placeholder="Enter pincode"
                  isRequired={true}
                  isInvalid={!!formErrors[`${formattedLabel}Pincode`]}
                  errorMessage={formErrors[`${formattedLabel}Pincode`]}
                />
              </FormControl>
            </Box>
            <FormControl flex="50%" className="form-input" isRequired>
              <PrimaryFormLabel htmlFor="PhoneNumber">
                Phone Number (in India)
              </PrimaryFormLabel>
              <Input
                type="number"
                id={`${formattedLabel}PhoneNumber`}
                name={`${formattedLabel}PhoneNumber`}
                value={formData[`${formattedLabel}PhoneNumber`]}
                onChange={(e) =>
                  handleChange(
                    formData,
                    setFormData,
                    formErrors,
                    setFormErrors,
                    e
                  )
                }
                placeholder="Enter your phone number"
                isInvalid={!!formErrors[`${formattedLabel}PhoneNumber`]}
                errorMessage={formErrors[`${formattedLabel}PhoneNumber`]}
                maxLength={40}
                isRequired={true}
              />
            </FormControl>
          </Box>
          <Box
            display="flex"
            flexDirection={{ base: "column", md: "row" }}
            justifyContent="space-between"
            className="form-box"
            mr={{ base: "0", md: "20px" }}
            width={label !== "Patient" && { base: "100%", md: "50%" }}
          >
            {label !== "Patient" && (
              <Box flex={"50%"}>
                <FormControl flex="50%" className="form-input" isRequired>
                  <PrimaryFormLabel htmlFor="Relationship">
                    Relationship
                  </PrimaryFormLabel>
                  <Input
                    type="text"
                    name={`${formattedLabel}Relationship`}
                    id={`${formattedLabel}Relationship`}
                    value={formData[`${formattedLabel}Relationship`]}
                    onChange={(e) =>
                      handleChange(
                        formData,
                        setFormData,
                        formErrors,
                        setFormErrors,
                        e
                      )
                    }
                    placeholder="Enter your relationship with patient"
                    isInvalid={!!formErrors[`${formattedLabel}Relationship`]}
                    errorMessage={formErrors[`${formattedLabel}Relationship`]}
                    maxLength={40}
                    isRequired={true}
                  />
                </FormControl>
              </Box>
            )}
          </Box>
        </Box>
      </Collapse>
    </>
  );
};

export default PatientAttendantSection;
